import type {
  DefineHandlebarTemplateForLayout,
  DefinePromotionTypeToMatchTheLayout,
  PromotionLayouts,
} from '@amplience/content-types/typings/s-plp-layout';
import type { Maybe, ProductPromotion } from '../../codegen/types';
import { formatAmountWithCurrency } from '../currencyHelper';

type LocalisedPromotionLayoutItem =
  | PromotionLayouts[number]
  | {
      promotionType?: DefinePromotionTypeToMatchTheLayout;
      handlebarTemplate?: DefineHandlebarTemplateForLayout;
      localisedHandlebarTemplate?: string;
      [k: string]: unknown;
    };

export type LocalisedPromotionLayouts = LocalisedPromotionLayoutItem[];

interface CreatePromotionHandlebarDataProps {
  promotionLayouts?: LocalisedPromotionLayouts;
  promotion?: Maybe<ProductPromotion>;
}

export interface CreatePromotionHandlebarDataResult {
  template: string;
  input: {
    price: string;
    discountCode?: Maybe<string>;
  };
  pricesStrikethrough: boolean;
}

export const createPromotionHandlebarData = ({
  promotionLayouts,
  promotion,
}: CreatePromotionHandlebarDataProps): CreatePromotionHandlebarDataResult | null => {
  if (!promotionLayouts?.length || !promotion) return null;

  // discountCode field is optional so if business specifies the promotion to be a "code" one without
  //  providing actual discount code to the cart discount then we're ending up with promotion
  //  message without code. This will ensure it's not being rendered until code is suppilied.
  if (promotion.layoutId.toLowerCase() === 'code' && !promotion.discountCode) return null;

  const matchingPromotion = promotionLayouts?.find(
    (promotionLayout) => promotionLayout.promotionType?.toLowerCase() === promotion?.layoutId?.toLowerCase(),
  );

  let matchingPromotionLayout;

  if (
    matchingPromotion?.localisedHandlebarTemplate &&
    typeof matchingPromotion.localisedHandlebarTemplate === 'string'
  ) {
    // Although promotionLayouts.localisedHandlebarTemplate is a an object with multiple values based on schema,
    // Amplience filter API returns a single string when query is localised
    matchingPromotionLayout = matchingPromotion.localisedHandlebarTemplate;
  } else if (matchingPromotion?.handlebarTemplate) {
    matchingPromotionLayout = matchingPromotion.handlebarTemplate;
  }

  const { minPrice, maxPrice, currencyCode } = promotion?.priceRange ?? {};

  if (!matchingPromotionLayout || !minPrice || !maxPrice) return null;

  // if min and max is different then it's split pricing and we have to build a price properly as
  //  we're supporting only 1 price tag in Amplience - {{price}}
  const price =
    minPrice !== maxPrice
      ? `${formatAmountWithCurrency(minPrice, currencyCode)} - ${formatAmountWithCurrency(maxPrice, currencyCode)}`
      : formatAmountWithCurrency(maxPrice, currencyCode);

  return {
    template: matchingPromotionLayout,
    input: {
      price,
      discountCode: promotion?.discountCode,
    },
    pricesStrikethrough: promotion.pricesStrikethrough,
  };
};
